import React, { ChangeEvent, cloneElement, ReactElement, useContext, useEffect, useState} from "react";
import ReactDOM from 'react-dom';
import { LFormInput, LFade, LDialog, LButton } from "@loffa/gpp-modules";
import {Connection, FileWriteBehavior,FileWriteBehaviorEnum,OutputDestination,OutputType,OutputTypeEnum} from "../types/outputDestinationsTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../../../AddEditAutoImportConfigurationsModal.module.css";
import { useAutoImportFormContext } from "../../../../Context/AutoImportFormContext";
import { LRequiredInput } from "../../CommonComponents/LRequiredInput";
import { FormGrid } from "../../CommonComponents/FormGrid";
import { getConnectionTypeIconAndLabel } from "../utils/getConnectionTypeIconAndLabel";
import { faExclamationTriangle, faPlus } from "@fortawesome/free-solid-svg-icons";
import { AddAutoImportConnectionModal } from "../../../../../AutoImportConnections/AddAutoImportConnectionModal";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";
import { AuthContext } from "src/context/AuthContext";

const outputTypeOptions = [
  { label: "Archive", value: OutputTypeEnum.ARCHIVE },
  { label: "Backup", value: OutputTypeEnum.BACKUP },
];

const fileWriteBehaviorOptions = [
  { label: "Overwrite", value: FileWriteBehaviorEnum.OVERWRITE },
  { label: "Add Numerator", value: FileWriteBehaviorEnum.ADDNUMERATOR },
  { label: "Skip", value: FileWriteBehaviorEnum.SKIP },
  { label: "Append", value: FileWriteBehaviorEnum.APPEND },
];

const ADD_CONNECTION_ID = "add_new_connection";

interface AddEditOutputDestinationModalProps {
  isOpen: boolean;
  initialData?: OutputDestination | null;
  onSave: (data: OutputDestination) => void;
  onClose: () => void;
}

const AddEditOutputDestinationModal = ({isOpen, initialData, onSave, onClose}:AddEditOutputDestinationModalProps) => {
  const { setError, error } = useAutoImportFormContext();
  const [outputType, setOutputType] = useState<OutputType | null>(initialData?.outputType ?? null);
  const [connection, setConnection] = useState<Connection | undefined>(initialData?.connection);
  const [additionalPath, setAdditionalPath] = useState<string>(initialData?.additionalPath || "");
  const [fileWriteBehavior, setFileWriteBehavior] = useState<FileWriteBehavior | undefined>(initialData?.fileWriteBehavior);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [showAddConnectionModal, setShowAddConnectionModal] = useState<boolean>(false);

  const authContext = useContext(AuthContext);
  const token = authContext.authState().accessToken.accessToken;

  const fetchConnections = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConnections`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch connections.");
      const result: Connection[] = await response.json();
      setConnections(result);
      setError(null);
    } catch (err: any) {
      setError("Error loading connections: " + err.message);
    }
  };
  
  useEffect(() => {
    fetchConnections();
  }, [token]);

  //adds the option to create a connection in the combobox
  const getConnectionsData = () => {
    const addOption = {
      id: ADD_CONNECTION_ID,
      description: "Add a connection",
      connectionTypeEnum: -1,
    } as Connection;
    return [addOption, ...connections];
  };

  const onOutputTypeChange = (e: KendoComboBoxEvent) => {
    setOutputType(e.target.value);
  };

  const onConnectionSelect = (e: KendoComboBoxEvent) => {
    if (e.target.value && e.target.value.id === ADD_CONNECTION_ID) {
      setShowAddConnectionModal(true);
      return;
    }
    setConnection(e.target.value as Connection);
  };

  const onAdditionalPathChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAdditionalPath(e.target.value);
  };

  const onFileWriteBehaviorSelect = (e: KendoComboBoxEvent) => {
    setFileWriteBehavior(e.target.value as FileWriteBehavior);
  };

  const connectionItemRender = (li: ReactElement<HTMLLIElement>, itemProps: { index: number; dataItem: Connection }) => {
    const { dataItem: conn } = itemProps;

    if (conn.id === ADD_CONNECTION_ID) {
      //option to add/create a connection directly in this step
      const content = (
        <div className={`${styles.connectionItemContainer} ${styles.addConnectionOption}`}>
          <div className={styles.connectionListTopRow}>
            <FontAwesomeIcon icon={faPlus} className={styles.icon} />
            <span className={styles.description}>{conn.description}</span>
          </div>
        </div>
      );
      return cloneElement(li, li.props, content);
    }
    
    //normal connection item rendering
    const { icon, label } = getConnectionTypeIconAndLabel(conn.connectionTypeEnum);
    const content = (
      <div className={styles.connectionItemContainer}>
        <div className={styles.connectionListTopRow}>
          <FontAwesomeIcon icon={icon} className={styles.icon} />
          <span className={styles.description}>{conn.description} - </span>
          <span>{label}</span>
        </div>
        <div className={styles.connectionListBottomRow}>
          <span>Server: {conn.server}</span>
          {conn.port && <span>Port: {conn.port}</span>}
          <span>Path: {conn.rootPath}</span>
        </div>
      </div>
    );
    return cloneElement(li, li.props, content);
  };

  const handleSave = (e: any) => {
    e.preventDefault();
    if (!outputType || !connection || !fileWriteBehavior) {
      setError("Please fill in all required fields.");
      return;
    }
    const newOutput: OutputDestination = {
      id: initialData?.id || crypto.randomUUID(),//temp id to track output destinations held in context state
      outputType,
      connection,
      additionalPath,
      fileWriteBehavior,
    };
    onSave(newOutput);
  };

  const handleClose = () => {
    setConnection(undefined);
    setAdditionalPath("");
    setFileWriteBehavior(undefined);
    setOutputType(null);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const inputs = [
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Output Type"
            labelAlign="top"
            required
            comboBox={{
              data: outputTypeOptions,
              textField: "label",
              dataItemKey: "value",
              value: outputType,
              onChange: onOutputTypeChange,
              placeholder: "-- Choose Output Type --",
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => <OutputTypeNote/>
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Connection"
            labelAlign="top"
            required
            comboBox={{
              data: getConnectionsData(),
              dataItemKey: "id",
              textField: "description",
              value: connection || null,
              onChange: onConnectionSelect,
              placeholder: "-- Select Connection --",
              itemRender: connectionItemRender,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <LFormInput
          type="input"
          labelAlign="top"
          labelName="Additional Path"
          input={{
            name: "Additional Path",
            placeholder: "Enter Additional Path (optional)",
            value: additionalPath,
            onChange: onAdditionalPathChange,
          }}
        />
      ),
    },
    {
      colSpan: 12,
      render: () => <AdditionalPathNote/>,
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="File Exists Behavior"
            labelAlign="top"
            required
            comboBox={{
              data: fileWriteBehaviorOptions,
              textField: "label",
              dataItemKey: "value",
              value: fileWriteBehavior,
              onChange: onFileWriteBehaviorSelect,
              placeholder: "-- Select File Write Behavior --",
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => <FileWriteBehaviorNote/>
    },
  ];

  const modalBody = (
    <div className={styles.addEditOutputModalContent}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      <form onSubmit={handleSave} className={styles.addEditOutputForm}>
        <FormGrid items={inputs} />
        <LButton type="submit" themeColor="primary">
            {initialData ? "Save" : "Add"}
        </LButton>
      </form>
    </div>
  );

  return (
    <>
      <LFade transitionEnterDuration={300}>
        <LDialog
          width="900"
          key={isOpen}
          title={`${initialData ? "Edit" : "Add"} Output Destination`}
          subtitle={<div className={styles.modalSubtitle}>Required Fields</div>}
          body={modalBody}
          actionCloseButton={handleClose}
          actionOverlay={handleClose}
        />
      </LFade>
      {/* allows users to create a new connection directly within the form */}
      {showAddConnectionModal && ReactDOM.createPortal(
        <AddAutoImportConnectionModal
          isOpen={showAddConnectionModal}
          onClose={() => setShowAddConnectionModal(false)}
          onSave={() => {
            fetchConnections();
            setShowAddConnectionModal(false);
          }}
        />,
        document.body
      )}
    </>
  );
};

// below are the info notes for the above form inputs 
const OutputTypeNote = () => {
  return (
    <div className={styles.deleteNote}>
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className={styles.warningIcon}
      />
      <span>
        <b>Note:</b> Available Options
        <ul>
          <li>
            <b>Archive</b> -&gt; Generates an output that includes all result files and original source file.
          </li>
          <li>
            <b>Backup</b> -&gt; Generates an output that includes only the source file.
          </li>
        </ul>
      </span>
    </div>
  );
};

const AdditionalPathNote = () => {
 return(
  <div className={styles.deleteNote}>
    <FontAwesomeIcon
      icon={faExclamationTriangle}
      className={styles.warningIcon}
    />
    <span>
      <b>Note:</b> You can use $(variable) to customize the output directory.
      Available variables are:
      <ul>
        <li>$(Date) {'->'} it will output the current date in the format yyyyMMdd</li>
        <li>$(Date:Format) {'->'} it will use the current data in the format provided. example $(Date:MMddYY)</li>
        <li>$(Datetime) {'->'} it will output the current date time in the format yyyyMMddHHmmss</li>
        <li>$(Datetime:Format) {'->'} it will use the current data in the format provided. example $(Date:MMddYYHHmmss)</li>
        <li>$(IndustryIdentifier) {'->'} it will output the configured Industry Identifier.</li>
        <li>$(Description) {'->'} it will output the configured Description.</li>
        <li>$(AutoImportType) {'->'} it will output the configured Auto import type.</li>
      </ul>
    </span>
  </div>
 )
}

const FileWriteBehaviorNote = () => {
  return (
    <div className={styles.deleteNote}>
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className={styles.warningIcon}
      />
      <span>
        <b>Note:</b> Available Options
        <ul>
          <li>
            <b>Overwrite</b> -&gt; Overwrites the file with new file.
          </li>
          <li>
            <b>Add Numerator</b> -&gt; (Default) Writes the file and Adds a File Numerator with format (N) to new file name.
          </li>
          <li>
            <b>Skip</b> -&gt; Does not write the file
          </li>
          <li>
            <b>Append Content</b> -&gt; Takes the new file content and attempts to add it to the end of the existing file
          </li>
        </ul>
      </span>
    </div>
  );
};

export { AddEditOutputDestinationModal };